/**
 * #11 — Landing-weight cross-check for ops surfaces.
 *
 * Reads the OFP estimated takeoff weight, subtracts trip burn, applies a
 * safety margin, then compares against the destination airport's edited
 * weight restrictions from Airport Manager (#10).
 *
 * Flag or block per opsDesk enforcement (flag default / block when Settings
 * say so). The enforcement mode can be extended later; for now the cross-check
 * always flags — matching the existing opsDesk pattern of advisory warnings.
 */
import { landingWeightFromOfp, type AirportManagerRecord, type LandingWeightCheck } from "./airportManager";
import type { Mission, AircraftSpec, MissionOfpAttachment } from "./types";

export type LandingWeightOpsCheck = LandingWeightCheck & {
  destIcao: string;
  aircraftMlwLb: number;
};

/**
 * Cross-check a board mission's OFP against its destination airport record.
 * Returns null when the OFP lacks the necessary fields or no airport record.
 */
export function checkMissionLandingWeight(
  mission: Pick<Mission, "destIcao" | "ofpAttachment">,
  destAirport: Pick<AirportManagerRecord, "weightRestriction"> | undefined | null,
  aircraft: Pick<AircraftSpec, "mlwLb"> | undefined | null,
): LandingWeightOpsCheck | null {
  const check = landingWeightFromOfp(mission.ofpAttachment, destAirport, aircraft?.mlwLb);
  if (!check) return null;

  return {
    ...check,
    destIcao: mission.destIcao,
    aircraftMlwLb: aircraft?.mlwLb ?? 0,
  };
}

/**
 * Build a dispatch warning string when the landing weight check fails.
 * Returns empty string when the check is OK or data is missing.
 */
export function landingWeightWarning(
  mission: Pick<Mission, "destIcao" | "ofpAttachment">,
  destAirport: Pick<AirportManagerRecord, "weightRestriction"> | undefined | null,
  aircraft: Pick<AircraftSpec, "mlwLb"> | undefined | null,
): string {
  const check = checkMissionLandingWeight(mission, destAirport, aircraft);
  if (!check || check.ok) return "";
  return check.note;
}

/**
 * Append landing-weight warning to an existing dispatchWarning string.
 */
export function appendLandingWeightWarning(
  existing: string | undefined,
  mission: Pick<Mission, "destIcao" | "ofpAttachment">,
  destAirport: Pick<AirportManagerRecord, "weightRestriction"> | undefined | null,
  aircraft: Pick<AircraftSpec, "mlwLb"> | undefined | null,
): string {
  const warning = landingWeightWarning(mission, destAirport, aircraft);
  if (!warning) return existing ?? "";
  if ((existing ?? "").includes(warning)) return existing ?? "";
  return [existing, warning].filter(Boolean).join(" · ");
}
